import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Modal, SearchableSelect, useToast } from '@kss/ui';
import { apiClient, type ApiFieldMeta, type ApiResourceMeta } from '@kss/api';
import {
  Save, X,
  // Per-resource icons make the popup visually distinct between
  // SalesOrder, SalesInvoice, SalesReturn etc. — otherwise every
  // "Add New" looks identical.
  Receipt, FileText, ShoppingCart, RefreshCcw, FileSpreadsheet,
  Truck, ClipboardList, Coins, Users, Building2, Boxes, Factory,
  Calculator, BookOpen, Tag, Coffee, ChefHat, ClipboardCheck,
  Ticket, Wallet, Layers, Briefcase, Award, Calendar, GraduationCap,
  Megaphone, Package, Database,
} from 'lucide-react';
import { getFkOptions, lookupFkTarget, type FkOption } from './fkResolver';
import { AuthImg } from '../../components/AuthImg';
import { useAuth } from '@kss/auth';
import { nextCode, provisionalCode, CODE_PREFIXES } from '../../utils/nextCode';

/**
 * Picks a contextual icon for the popup header based on the entity
 * name. Falls back to a generic database icon. Keeps the registry
 * lean — we just match on common entity-name tokens.
 */
export function pickIcon(name: string) {
  const n = name.toLowerCase();
  if (/sales.*order|salesorder/.test(n))           return ShoppingCart;
  if (/sales.*invoice|invoice$/.test(n))           return Receipt;
  if (/sales.*return|salesreturn|return$/.test(n)) return RefreshCcw;
  if (/credit.*note|creditnote/.test(n))           return FileSpreadsheet;
  if (/debit.*note|debitnote/.test(n))             return FileSpreadsheet;
  if (/delivery.*challan|challan/.test(n))         return Truck;
  if (/payment/.test(n))                           return Coins;
  if (/purchase.*order|purchaseorder/.test(n))     return ShoppingCart;
  if (/purchase.*invoice/.test(n))                 return Receipt;
  if (/goods.*receipt|gr[nN]/.test(n))             return ClipboardCheck;
  if (/stock|inventory.*movement/.test(n))         return Boxes;
  if (/transfer/.test(n))                          return Truck;
  if (/adjustment/.test(n))                        return RefreshCcw;
  if (/requisition/.test(n))                       return ClipboardList;
  if (/rfq/.test(n))                               return FileText;
  if (/customer/.test(n))                          return Users;
  if (/supplier|vendor/.test(n))                   return Truck;
  if (/employee/.test(n))                          return Briefcase;
  if (/lead|opportunity/.test(n))                  return Megaphone;
  if (/quotation/.test(n))                         return FileText;
  if (/campaign/.test(n))                          return Megaphone;
  if (/ticket/.test(n))                            return Ticket;
  if (/wallet|cash/.test(n))                       return Wallet;
  if (/menu.*item|recipe/.test(n))                 return Coffee;
  if (/menu.*category|category/.test(n))           return Layers;
  if (/restaurant.*table|table/.test(n))           return ClipboardList;
  if (/kot/.test(n))                               return ChefHat;
  if (/work.*order|production/.test(n))            return Factory;
  if (/bill.*of.*material|bom/.test(n))            return Package;
  if (/journal|ledger/.test(n))                    return BookOpen;
  if (/account/.test(n))                           return Calculator;
  if (/tax/.test(n))                               return Calculator;
  if (/budget/.test(n))                            return BookOpen;
  if (/attendance|leave|payroll|payslip/.test(n))  return Calendar;
  if (/training/.test(n))                          return GraduationCap;
  if (/candidate|interview|recruitment/.test(n))   return Users;
  if (/performance|kpi/.test(n))                   return Award;
  if (/asset/.test(n))                             return Building2;
  if (/brand|unit/.test(n))                        return Tag;
  if (/company|branch|department|designation/.test(n)) return Building2;
  if (/product/.test(n))                           return Package;
  return Database;
}

/** Short, friendly description shown under the popup heading. */
export function pickDescription(name: string, group: string): string {
  const n = name.toLowerCase();
  if (/sales.*order/.test(n))      return 'Capture a new sales order against a customer.';
  if (/sales.*invoice/.test(n))    return 'Issue an invoice for delivered goods or services.';
  if (/sales.*return/.test(n))     return 'Record returned goods from a customer.';
  if (/credit.*note/.test(n))      return 'Issue a credit note reducing the customer’s outstanding balance.';
  if (/debit.*note/.test(n))       return 'Issue a debit note increasing the customer’s outstanding balance.';
  if (/delivery.*challan/.test(n)) return 'Log goods despatched to a customer (pre-invoice).';
  if (/purchase.*order/.test(n))   return 'Raise a purchase order on a supplier.';
  if (/goods.*receipt/.test(n))    return 'Record receipt of goods against a purchase order.';
  if (/stock.*transfer/.test(n))   return 'Move stock between warehouses or branches.';
  if (/stock.*adjustment/.test(n)) return 'Reconcile physical stock with system balance.';
  if (/customer/.test(n))          return 'Add a new customer to the master list.';
  if (/supplier/.test(n))          return 'Add a new supplier to the master list.';
  if (/employee/.test(n))          return 'Onboard a new employee.';
  if (/journal/.test(n))           return 'Post a journal voucher to the general ledger.';
  return `Add a new ${humanise(name).toLowerCase()} in the ${group} module.`;
}

/**
 * Registry-driven CRUD form. Given an `ApiResourceMeta` (and optionally an
 * existing row to edit) it renders one input per field, posts to
 * `meta.basePath` on save, and reports success / failure via the toast +
 * `onSaved` callback. The field metadata is the same one emitted by the
 * generator from the live swagger spec, so this component supports
 * **all 287 resources** without per-resource code.
 */
export interface GenericCrudFormProps {
  meta: ApiResourceMeta;
  /** Existing row for edit; omit to create a new row. */
  row?: Record<string, unknown> | null;
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export const HIDDEN_ON_CREATE = new Set([
  'tenantId', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'deletedAt', 'deletedBy', 'indate',
]);

/**
 * Fields the operator should NEVER see in the form — neither on
 * create nor on edit. These are either:
 *   - audit-trail (createdBy/updatedAt/etc.) — server-managed
 *   - primary keys (id, tenantId) — opaque
 *   - the legacy 0/1 isActive boolean — replaced by the friendlier
 *     Active/Passive/Deleted dropdown bound to `status` (see below).
 */
export const HIDDEN_ALWAYS = new Set([
  'createdBy', 'updatedAt', 'updatedBy',
  'deletedAt', 'deletedBy',
  'tenantId', 'id',
  'isActive',
  // `indate` is the server-stamped audit timestamp — never user-editable.
  'indate', 'createdAt',
  // `remarks` — optional free-text note. Per request it's hidden on
  // EVERY entry form (and every grid, see ApiBrowser.HIDDEN_GRID_COLUMNS).
  // The column still exists server-side; we just don't surface an input.
  'remarks',
]);

/**
 * Tri-state status options. The live API now stores `status` as a
 * numeric enum (no separate `isActive` boolean), per the 2026-05-23
 * codegen — refer to `apiRegistry.ts`. Codes:
 *   0 → Active
 *   1 → Passive
 *   2 → Deleted
 */
export const STATUS_OPTIONS: Array<{ label: string; value: number }> = [
  { label: 'Active',  value: 0 },
  { label: 'Passive', value: 1 },
  { label: 'Deleted', value: 2 },
];

/**
 * Field-name → static option list. Used by FieldInput to render a
 * `<select>` instead of a plain text input for known string-enum
 * columns. Add a new entry whenever a registry field is conceptually
 * an enum but the swagger says `kind: string`.
 */
export const STATIC_ENUMS: Record<string, string[]> = {
  // Money
  currency: ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD', 'JPY'],
  // Units of measure
  unitOfMeasure: ['PCS', 'KG', 'GM', 'LTR', 'ML', 'BOX', 'CRB', 'BRL', 'BKT', 'NOS', 'METER', 'INCH', 'PAIR', 'SET', 'DOZEN', 'GROSS'],
  unit:          ['PCS', 'KG', 'GM', 'LTR', 'ML', 'BOX', 'CRB', 'BRL', 'BKT', 'NOS', 'METER', 'INCH', 'PAIR', 'SET', 'DOZEN', 'GROSS'],
  // Geography
  country: ['India', 'United States', 'United Kingdom', 'United Arab Emirates', 'Singapore', 'Australia', 'Canada', 'Germany'],
  // Indian states + UTs — rendered as a searchable dropdown; PIN lookup
  // auto-fills this. allowCustom (set in the render branch) lets the user
  // type a value not in the list.
  state: [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
    'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
    'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
    'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Puducherry',
    'Chandigarh', 'Andaman & Nicobar Islands', 'Dadra & Nagar Haveli and Daman & Diu', 'Lakshadweep',
  ],
  // Workflow / lifecycle
  paymentStatus: ['Unpaid', 'Partial', 'Paid', 'Overdue', 'Refunded', 'Cancelled'],
  invoiceStatus: ['Draft', 'Posted', 'Cancelled', 'Paid'],
  orderStatus:   ['Draft', 'Confirmed', 'Processing', 'Shipped', 'Delivered', 'Cancelled', 'Returned'],
  paymentMethod: ['Cash', 'UPI', 'Card', 'NetBanking', 'Cheque', 'Wallet', 'Other'],
  // PaymentMethod entity's type column is `methodType` (not paymentMethod).
  methodType: ['Card', 'UPI', 'NetBanking', 'Wallet', 'Cash', 'Cheque', 'BankTransfer', 'Other'],
  // Restaurant
  tableStatus: ['Available', 'Occupied', 'Reserved', 'Cleaning', 'OutOfService'],
  // Attendance (composite enum; FE uses string until backend exposes int)
  attendanceStatus: ['Present', 'Absent', 'Leave', 'HalfDay', 'Holiday', 'WeeklyOff'],
  // Sales/Purchase document workflow statuses (registry forms #8/#15/#16/#17/#19/#20)
  shipmentStatus: ['Ready', 'InTransit', 'Delivered', 'Returned', 'Lost', 'Cancelled'],
  refundStatus: ['Pending', 'Processing', 'Refunded', 'Rejected', 'Cancelled'],
  approvalStatus: ['Pending', 'Approved', 'Rejected', 'Converted', 'Cancelled', 'Recalled'],
  inspectionStatus: ['Pending', 'Passed', 'Failed', 'Partial', 'OnHold'],
  adjustmentType: ['Cycle', 'Damage', 'Loss', 'Found', 'Revaluation', 'System'],
  adjustmentStatus: ['Draft', 'Posted', 'Cancelled'],
  transferStatus: ['Draft', 'InTransit', 'Received', 'Cancelled'],
  // Marketing
  promotionType: ['Discount', 'Flash', 'Seasonal', 'BundleDeal', 'BOGO', 'Clearance'],
  discountType: ['Percentage', 'Flat', 'FreeDelivery', 'BuyXGetY', 'Bundle'],
  couponStatus: ['Active', 'Inactive', 'Expired', 'Exhausted'],
  giftCardStatus: ['Active', 'Redeemed', 'Expired', 'Blocked'],
};

/** Coerce arbitrary input (string label OR numeric code) → numeric code. */
export function isActiveFromStatus(status: unknown): number {
  if (typeof status === 'number') return status;
  const s = String(status ?? '').toLowerCase();
  if (s === '1' || s === 'passive') return 1;
  if (s === '2' || s === 'deleted') return 2;
  return 0; // active (default)
}

export function statusFromIsActive(value: unknown): number {
  return isActiveFromStatus(value);
}

/**
 * Derive a short uppercase prefix from an entity name. Used by the
 * Code field's auto-generator. ProductCategory → "PC". Customer → "CUS"
 * (fallback: first 3 chars when fewer than 2 capitals are present).
 */
export function entityCodePrefix(entityName: string): string {
  const caps = entityName.replace(/[^A-Z]/g, '');
  if (caps.length >= 2) return caps.slice(0, 4);
  return entityName.slice(0, 3).toUpperCase();
}

/** "PC" → "PC-LNKM9" — short, monotonic, unique within a session. */
export function makeCode(prefix: string): string {
  const suffix = Date.now().toString(36).toUpperCase().slice(-5);
  return `${prefix}-${suffix}`;
}

/**
 * Ordered ranking for the generic form layout. Lower number = earlier
 * on the page. Unmatched field names fall in the middle (rank 50).
 * `status` is pinned just before `isActive` so the two appear as a
 * pair near the bottom of every form (per the demo design).
 */
const FIELD_RANK: Record<string, number> = {
  code: 1, productCode: 1,
  name: 2,
  parentMenuId: 3, parentId: 3, parentCategoryId: 3, parentProductCategoryId: 3,
  sortOrder: 4, displayOrder: 4, displayOrderInGroup: 4,
  // Description / Remarks / Notes are OPTIONAL free-text — per user
  // request they sit at the BOTTOM of every form, just above the
  // status pair + Save row (ranks 95-97, before status=98/isActive=99).
  description: 95,
  notes: 96,
  remarks: 97,
  // Address block — user-requested order: address → pincode → state →
  // city → country. PIN drives the city/state/country auto-fill.
  address: 60, billingAddress: 60, shippingAddress: 61, addressLine1: 60, addressLine2: 61,
  pincode: 62, pinCode: 62, postalCode: 62, postCode: 62, zip: 62, zipCode: 62,
  state: 63, province: 63, stateName: 63, region: 63,
  city: 64, town: 64, cityName: 64,
  country: 65, countryName: 65, countryId: 65,
  // Image-ish columns next-to-last so the previews don't push status
  // off the visible area.
  imageUrl: 90, imageUrls: 90, photoUrl: 90, logoUrl: 90, avatarUrl: 90, pictureUrl: 90,
  // Status pair at the very bottom.
  status: 98,
  isActive: 99,
};

export function orderFields<T extends { name: string }>(fields: T[]): T[] {
  const rank = (name: string): number =>
    FIELD_RANK[name] ?? (/image|photo|avatar|logo|picture|thumbnail/i.test(name) ? 90 : 50);
  return [...fields].sort((a, b) => {
    const ra = rank(a.name);
    const rb = rank(b.name);
    if (ra !== rb) return ra - rb;
    return a.name.localeCompare(b.name);
  });
}

const inputCls =
  'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-9 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const textareaCls =
  'min-h-[80px] w-full rounded-md border border-border bg-surface-card px-3 py-2 font-mono text-xs text-content-primary outline-none focus:border-primary';

export function GenericCrudForm({ meta, row, open, onClose, onSaved }: GenericCrudFormProps) {
  const isEdit = !!row;
  const toast = useToast();

  const visibleFields = useMemo(
    () => {
      // Same legacy-FK dedupe as GenericFullPageForm — hide string
      // `department` when `departmentId` (FK) exists, etc.
      const fkStems = new Set<string>();
      for (const f of meta.fields) {
        if (f.kind === 'number' && f.name.endsWith('Id') && !f.isId) {
          fkStems.add(f.name.slice(0, -2));
        }
      }
      return orderFields(
        meta.fields.filter((f) => {
          if (HIDDEN_ALWAYS.has(f.name)) return false;
          if (f.isId && f.name !== 'code') return false;
          if (HIDDEN_ON_CREATE.has(f.name)) return isEdit;
          if (f.kind === 'string' && fkStems.has(f.name)) return false;
          return true;
        }),
      );
    },
    [meta.fields, isEdit],
  );

  const [form, setForm] = useState<Record<string, unknown>>(() => seedForm(visibleFields, row));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset when the row or meta changes.
  useEffect(() => {
    if (open) {
      setForm(seedForm(visibleFields, row));
      setErr(null);
    }
  }, [open, row, visibleFields]);

  function set(name: string, value: unknown) {
    setForm((f) => ({ ...f, [name]: value }));
  }

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      const payload = await coerceWithImages(form, visibleFields, isEdit);
      if (isEdit) {
        await apiClient.put(meta.basePath, payload);
        toast.success('Saved', `${meta.name} updated`);
      } else {
        await apiClient.post(meta.basePath, payload);
        toast.success('Saved', `${meta.name} created`);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setErr(msg);
      toast.error('Save failed', msg);
    } finally {
      setSaving(false);
    }
  }

  const Icon = pickIcon(meta.name);
  const title = (
    // Icon + name + description, so SalesOrder vs SalesInvoice vs
    // SalesReturn no longer look identical when opened.
    <div className="flex items-center gap-3">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-content-primary">
          {isEdit ? 'Edit' : 'New'} {humanise(meta.name)}
        </div>
        <div className="truncate text-[11px] text-content-muted">
          {pickDescription(meta.name, meta.group)}
        </div>
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title={title} width={720}>
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}

      {/* Field grid collapses to one column on phones automatically
          (md: prefix). Buttons row also wraps on narrow screens. */}
      <div className="grid gap-3 sm:grid-cols-2">
        {visibleFields.map((f) => (
          <FieldInput
            key={f.name}
            field={f}
            value={form[f.name]}
            onChange={(v) => set(f.name, v)}
            selfResource={meta}
          />
        ))}
      </div>

      <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-border pt-3">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          <X className="h-4 w-4" /> Cancel
        </Button>
        <Button onClick={save} disabled={saving}>
          <Save className="h-4 w-4" /> {saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}
        </Button>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------- */
/*  Field renderer                                                      */
/* -------------------------------------------------------------------- */

export function FieldInput({
  field,
  value,
  onChange,
  selfResource,
}: {
  field: ApiFieldMeta;
  value: unknown;
  onChange: (next: unknown) => void;
  selfResource?: ApiResourceMeta;
}) {
  // FK detection — used for both label-stripping ("Parent Category Id"
  // → "Parent Category") and the SearchableSelect render below.
  const fkTarget = !field.isId && field.kind === 'number'
    ? lookupFkTarget(field.name, { selfResource })
    : null;
  const labelText = fkTarget
    ? humanise(field.name.replace(/Id$/, ''))
    : humanise(field.name);
  const label = (
    <span className="text-xs font-medium text-content-secondary">
      {labelText}
      {field.required && <span className="text-danger"> *</span>}
      {field.isId && <span className="ml-1 text-[10px] text-content-muted">(id)</span>}
    </span>
  );

  // Code — auto-incremented, prefix-locked, read-only for non-admins.
  // Runs BEFORE the generic read-only-ID branch so this dedicated
  // renderer wins for the `code` column (swagger marks it isId).
  if (field.name === 'code') {
    return (
      <CodeField
        value={value}
        onChange={onChange}
        label={label}
        maxLength={field.maxLength}
        selfResource={selfResource}
      />
    );
  }

  // Read-only generic ID fields on EDIT (anything else flagged isId).
  if (field.isId) {
    return (
      <label className="flex flex-col gap-1">
        {label}
        <input className={inputCls} value={String(value ?? '')} readOnly />
      </label>
    );
  }

  if (field.name === 'status') {
    const currentCode = isActiveFromStatus(value);
    return (
      <div className="flex flex-col gap-1">
        {label}
        <SearchableSelect
          value={String(currentCode)}
          onChange={(v) => onChange(Number(v))}
          options={STATUS_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))}
          placeholder="Active"
        />
      </div>
    );
  }

  // Static-enum string fields — Currency / UnitOfMeasure / Country /
  // PaymentStatus etc. The registry stores these as `kind: string`, but
  // the operator wants a dropdown not a free-text input.
  //
  // 2026-05-29: per user feedback, swapped from a plain `<select>` to a
  // SearchableSelect typeahead so common enums (Currency: INR/USD/EUR,
  // UoM: PCS/KG/LTR, Country, etc.) get a type-to-filter experience
  // instead of having to scroll a long native dropdown.
  const staticEnum = STATIC_ENUMS[field.name];
  if (staticEnum && field.kind === 'string') {
    return (
      <div className="flex flex-col gap-1">
        {label}
        <SearchableSelect
          value={String(value ?? '')}
          onChange={(v) => onChange(v === '' ? null : v)}
          options={staticEnum.map((opt) => ({ value: opt, label: opt }))}
          placeholder="— select —"
          searchPlaceholder="Type to filter…"
          allowCustom
        />
      </div>
    );
  }

  if (field.kind === 'boolean') {
    return (
      <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-surface-card px-3 py-2 text-sm">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 accent-primary"
        />
        <span>{humanise(field.name)}</span>
      </label>
    );
  }

  // Foreign-key fields: render a typeahead-searchable dropdown of
  // names, not a raw integer input. Catches `departmentId`,
  // `designationId`, `userId`, `parentId`, `reportingManagerId`, etc.
  // Falls back to a number input if the target resource can't be
  // resolved (alias missing) — fkTarget was already resolved above.
  if (fkTarget) {
    return (
      <FkSelect
        field={field}
        value={value}
        onChange={onChange}
        selfResource={selfResource}
        label={label}
      />
    );
  }

  if (field.kind === 'number') {
    // GST rate columns (`taxRate`, `cgstRate`, `sgstRate`, `igstRate`,
    // `vatRate`) — render a typeahead with the common Indian GST slabs
    // but allow custom (so users can still type "12.5" if the backend
    // tracks fractional rates).
    if (/(taxRate|gstRate|cgstRate|sgstRate|igstRate|vatRate|salesTaxRate|purchaseTaxRate)$/i.test(field.name)) {
      const options = ['0', '0.25', '3', '5', '12', '18', '28'].map((v) => ({
        value: v, label: `${v}%`,
      }));
      return (
        <div className="flex flex-col gap-1">
          {label}
          <SearchableSelect
            value={value == null ? '' : String(value)}
            onChange={(v) => onChange(v === '' ? null : Number(v))}
            options={options}
            placeholder="Select or type a rate"
            searchPlaceholder="Type a rate (e.g. 18 or 12.5)…"
            allowCustom
          />
        </div>
      );
    }
    // The API frequently uses 0/1 for isActive — render a select for that
    if (field.name === 'isActive') {
      return (
        <label className="flex flex-col gap-1">
          {label}
          <select
            className={selectCls}
            value={String(value ?? 1)}
            onChange={(e) => onChange(Number(e.target.value))}
          >
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </label>
      );
    }
    return (
      <label className="flex flex-col gap-1">
        {label}
        <input
          type="number"
          className={inputCls}
          value={value == null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      </label>
    );
  }

  if (field.kind === 'datetime') {
    return (
      <label className="flex flex-col gap-1">
        {label}
        <DateField value={value} onChange={onChange} />
      </label>
    );
  }

  if (field.kind === 'enum' && field.enum) {
    return (
      <label className="flex flex-col gap-1">
        {label}
        <select
          className={selectCls}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value || null)}
        >
          <option value="">—</option>
          {field.enum.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (field.kind === 'email') {
    return (
      <label className="flex flex-col gap-1">
        {label}
        <input
          type="email"
          className={inputCls}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          maxLength={field.maxLength}
        />
      </label>
    );
  }

  if (field.kind === 'array' || field.kind === 'object') {
    // Free-form JSON for nested shapes
    return (
      <label className="flex flex-col gap-1 sm:col-span-2">
        {label}
        <textarea
          className={textareaCls}
          value={value == null ? '' : safeJson(value)}
          onChange={(e) => onChange(parseJsonSilently(e.target.value))}
          placeholder={field.kind === 'array' ? '[]' : '{}'}
        />
      </label>
    );
  }

  // Image / photo / logo / avatar fields → upload widget with preview.
  // Accepts files up to VITE_IMAGE_MAX_KB (default 50 KB) and stores
  // the data URI inline in the form. Multi-image fields (anything
  // pluralised: `imageUrls`, `photos`) keep an array of URIs.
  if (/image|photo|avatar|logo|picture|thumbnail/i.test(field.name)) {
    // `imageUrls` / `photos` (plural) accept multiple images; the
     // singular forms (`imageUrl`, `logoUrl`) cap at one.
    const multi = /s$/i.test(field.name);
    return (
      <div className="flex flex-col gap-1 sm:col-span-2">
        {label}
        <ImageUploadField value={value} onChange={onChange} multiple={multi} />
      </div>
    );
  }

  // string / uuid / fallback
  const isLong = (field.maxLength ?? 0) > 200 || /description|remarks|notes/i.test(field.name);
  const isOptionalNote = /^(description|remarks|notes)$/i.test(field.name) && !field.required;
  return (
    <label className={`flex flex-col gap-1 ${isLong ? 'sm:col-span-2' : ''}`}>
      {isOptionalNote ? (
        <span className="text-xs font-medium text-content-secondary">
          {labelText} <span className="text-content-muted">(optional)</span>
        </span>
      ) : label}
      {isLong ? (
        <textarea
          className={textareaCls.replace('font-mono text-xs', 'text-sm')}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          maxLength={field.maxLength}
        />
      ) : (
        <input
          className={inputCls}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          maxLength={field.maxLength}
          placeholder={field.kind === 'uuid' ? 'auto-generated if blank' : undefined}
        />
      )}
    </label>
  );
}

/**
 * Auto-incrementing, prefix-locked Code field.
 *
 *   - On a NEW record (value empty) it fetches the resource list and
 *     fills the next code: `<PREFIX>-00001`, `<PREFIX>-00002`, …
 *     (max existing numeric suffix + 1, zero-padded to 5).
 *   - The field is READ-ONLY for non-admins (so codes stay sequential);
 *     admins get an editable input + a "Prefix" mini-editor so only the
 *     prefix can be changed and the number re-derives.
 *   - On EDIT (value already set) it shows the existing code read-only
 *     for everyone — codes are immutable identifiers once created.
 *
 * Prefix source: CODE_PREFIXES[entity] → else derived from capitals.
 */
function CodeField({
  value, onChange, label, maxLength, selfResource,
}: {
  value: unknown;
  onChange: (next: unknown) => void;
  label: React.ReactNode;
  maxLength?: number;
  selfResource?: ApiResourceMeta;
}) {
  const { user } = useAuth();
  const isAdmin = /admin/i.test(String(user?.role ?? ''));
  const entity = selfResource?.name ?? '';
  const defaultPrefix = CODE_PREFIXES[entity] ?? entityCodePrefix(entity);
  const [prefix, setPrefix] = useState(defaultPrefix);
  const hadValueOnMount = useRef(!!String(value ?? '').trim());
  const filledRef = useRef(false);

  // Auto-fill the next code on a fresh record (only once, only when empty).
  useEffect(() => {
    if (hadValueOnMount.current || filledRef.current) return;
    if (String(value ?? '').trim()) return;
    if (!selfResource?.basePath) { onChange(provisionalCode(prefix)); filledRef.current = true; return; }
    filledRef.current = true;
    let cancelled = false;
    onChange(provisionalCode(prefix)); // instant placeholder
    void (async () => {
      const code = await nextCode(selfResource.basePath, prefix);
      if (!cancelled) onChange(code);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When an admin changes the prefix, re-derive the next code.
  async function applyPrefix(next: string) {
    setPrefix(next);
    if (selfResource?.basePath) {
      const code = await nextCode(selfResource.basePath, next);
      onChange(code);
    } else {
      onChange(provisionalCode(next));
    }
  }

  const isEditExisting = hadValueOnMount.current;
  const locked = isEditExisting || !isAdmin;

  return (
    <label className="flex flex-col gap-1">
      {label}
      {locked ? (
        <input
          className={`${inputCls} cursor-not-allowed bg-surface-muted text-content-muted`}
          value={String(value ?? '')}
          readOnly
          placeholder={`${prefix}-00001`}
        />
      ) : (
        // Prefix + Code on a SINGLE line: the prefix mini-input sits to
        // the left of the auto-incrementing code. Editing the prefix
        // re-derives the next number.
        <div className="flex items-center gap-2">
          <input
            className="h-9 w-20 shrink-0 rounded-md border border-border bg-surface-card px-2 text-sm text-content-primary outline-none focus:border-primary"
            value={prefix}
            onChange={(e) => void applyPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            title="Prefix — the number auto-increments"
            aria-label="Code prefix"
          />
          <input
            className={`${inputCls} flex-1`}
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
            maxLength={maxLength}
            placeholder={`${prefix}-00001`}
          />
        </div>
      )}
      {!locked && (
        <span className="text-[10px] text-content-muted">Prefix is editable — the number auto-increments.</span>
      )}
      {locked && (
        <p className="text-[10px] text-content-muted">
          {isEditExisting ? 'Code is fixed once created.' : 'Auto-generated. Only an admin can change it.'}
        </p>
      )}
    </label>
  );
}

/**
 * Image upload widget. Reads files via FileReader → data URI, gates
 * by `VITE_IMAGE_MAX_KB` (default 50 KB), shows a thumbnail strip with
 * remove buttons. Supports either a single URL or an array of URLs
 * depending on the `multiple` prop.
 *
 * Resulting form value is either a string (single) or string[] (multi).
 * Existing URLs (from EDIT load) keep working — they're displayed as
 * <img src=...> too.
 */
export function ImageUploadField({
  value, onChange, multiple,
}: {
  value: unknown;
  onChange: (next: unknown) => void;
  multiple: boolean;
}) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const env = (import.meta as unknown as { env?: Record<string, string> }).env ?? {};
  const maxKb = Number(env.VITE_IMAGE_MAX_KB ?? 500);
  // G56 closed 2026-05-29: backend restored POST /api/Image/upload, so
  // we no longer have to inline-base64 images into the entity's
  // imageUrl column. persistImage() (called at save time) uploads the
  // data: URL and replaces it with the hosted /uploads/... URL — that
  // round-trip is short enough that the inline char limit only matters
  // as a defensive cap (preview blob size) and we can be generous.
  const MAX_DATA_URL_CHARS = Number(env.VITE_IMAGE_MAX_INLINE_CHARS ?? 4_000_000);

  const list: string[] = multiple
    ? (Array.isArray(value) ? value as string[] : (value ? [String(value)] : []))
    : (value ? [String(value)] : []);

  /**
   * Read a File, draw to canvas, re-export as JPEG, halving dimensions
   * until the resulting data URL fits in `MAX_DATA_URL_CHARS`. Returns
   * the smallest dataURL that fits, or null if even a 16x16 thumbnail
   * doesn't fit (in which case the user must pick a smaller source).
   */
  async function shrinkToFitDataUrl(f: File): Promise<string | null> {
    const bitmap = await (typeof createImageBitmap === 'function'
      ? createImageBitmap(f).catch(() => null)
      : null);
    let w = bitmap?.width ?? 256;
    let h = bitmap?.height ?? 256;
    let quality = 0.85;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!bitmap || !ctx) return null;
    for (let pass = 0; pass < 8; pass++) {
      canvas.width = w;
      canvas.height = h;
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(bitmap, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      if (dataUrl.length <= MAX_DATA_URL_CHARS) return dataUrl;
      if (w <= 16 || h <= 16) break;
      w = Math.max(16, Math.floor(w * 0.6));
      h = Math.max(16, Math.floor(h * 0.6));
      quality = Math.max(0.4, quality - 0.1);
    }
    return null;
  }

  const accept = async (files: FileList) => {
    setError(null);
    const out: string[] = [...list];
    for (const f of Array.from(files)) {
      const sizeKb = f.size / 1024;
      if (sizeKb > maxKb) {
        setError(`"${f.name}" is ${sizeKb.toFixed(1)} KB — limit is ${maxKb} KB. Resize before uploading.`);
        continue;
      }
      // Naive base64 read first — fast path for already-tiny images.
      const dataUri = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = () => reject(r.error);
        r.readAsDataURL(f);
      });
      let final = dataUri;
      if (dataUri.length > MAX_DATA_URL_CHARS) {
        // Auto-shrink so we fit in the narrow imageUrl column (G56).
        const shrunk = await shrinkToFitDataUrl(f);
        if (!shrunk) {
          setError(`"${f.name}" cannot be shrunk small enough to inline. Pick a simpler / smaller image until the image-upload endpoint is restored (backend gap G56).`);
          continue;
        }
        final = shrunk;
      }
      out.push(final);
      if (!multiple) break;
    }
    onChange(multiple ? out : (out[0] ?? null));
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeAt = (idx: number) => {
    const next = list.filter((_, i) => i !== idx);
    onChange(multiple ? next : (next[0] ?? null));
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {list.map((src, i) => (
          <div key={i} className="relative h-20 w-20 overflow-hidden rounded border border-border bg-surface-card">
            <AuthImg
              src={resolveImageSrc(src)}
              className="h-full w-full object-cover"
            />
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="absolute right-0.5 top-0.5 grid h-5 w-5 place-items-center rounded-full bg-black/60 text-white hover:bg-black/80"
              aria-label="Remove image"
            >
              ×
            </button>
          </div>
        ))}
        {(multiple || list.length === 0) && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded border border-dashed border-border text-[10px] text-content-muted hover:border-primary hover:text-primary"
          >
            <span className="text-lg leading-none">+</span>
            <span>Upload</span>
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        multiple={multiple}
        onChange={(e) => { if (e.target.files) void accept(e.target.files); }}
      />
      <p className="text-[11px] text-content-muted">
        Max size <strong>{maxKb} KB</strong> per image · {multiple ? 'multiple images allowed' : 'single image'}
        {' · configurable via '}<code>VITE_IMAGE_MAX_KB</code>
      </p>
      {error && <p className="text-[11px] text-danger">{error}</p>}
    </div>
  );
}

/**
 * Dropdown bound to a foreign-key field. Loads the target entity's
 * rows on mount, renders the option list once they arrive. Keeps the
 * current numeric value selected so an existing record's FK isn't
 * silently wiped if the user opens the edit modal before options have
 * resolved.
 */
/**
 * Date input with three affordances:
 *  1. Type dd-MM-yyyy in the textbox. Dashes auto-insert after the 2nd
 *     and 5th character so the user never has to type them.
 *  2. Click the 📅 button to open the browser's native date picker.
 *  3. On blur, the value is validated — invalid dates clear the
 *     stored ISO and show an inline error. Valid dates round-trip
 *     to the backend as ISO 8601 strings.
 *
 * On a fresh create (no incoming value), the field auto-fills with
 * today's date — matches the customer's "date should be auto fill" spec.
 */
function DateField({
  value, onChange,
}: { value: unknown; onChange: (next: unknown) => void }) {
  const initial = useMemo(() => toDDMMYYYY(value), [value]);
  const [text, setText] = useState<string>(initial);
  const [err, setErr] = useState<string | null>(null);
  const nativeRef = useRef<HTMLInputElement | null>(null);

  // Auto-fill today on a fresh form (no value, no typed text).
  useEffect(() => {
    if (!value && !text) {
      const today = toDDMMYYYY(new Date().toISOString());
      setText(today);
      onChange(fromDDMMYYYY(today));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-sync if parent value changes (e.g. EDIT load).
  useEffect(() => {
    if (typeof value === 'string' && value) {
      const formatted = toDDMMYYYY(value);
      if (formatted && formatted !== text) setText(formatted);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function onTextChange(raw: string) {
    // Strip everything except digits, then re-insert dashes after pos 2 + 5.
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    let next = digits;
    if (digits.length > 2) next = digits.slice(0, 2) + '-' + digits.slice(2);
    if (digits.length > 4) next = digits.slice(0, 2) + '-' + digits.slice(2, 4) + '-' + digits.slice(4);
    setText(next);
    setErr(null);
    // While still typing, only emit ISO once we have a complete date.
    if (digits.length === 8) {
      const iso = fromDDMMYYYY(next);
      if (iso) {
        onChange(iso);
      } else {
        setErr('Invalid date');
      }
    } else if (digits.length === 0) {
      onChange(null);
    }
  }

  function onBlur() {
    if (!text) return;
    const iso = fromDDMMYYYY(text);
    if (!iso) {
      setErr('Enter a valid date in dd-MM-yyyy');
      return;
    }
    onChange(iso);
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          maxLength={10}
          placeholder="dd-MM-yyyy"
          className={`${inputCls} pr-9`}
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          onBlur={onBlur}
        />
        <button
          type="button"
          onClick={() => {
            // Open the native date picker. `showPicker()` only works on a
            // visible, interactive input — our overlay below IS that (it's
            // transparent but full-size, opacity-0, pointer-events-auto).
            const native = nativeRef.current;
            if (!native) return;
            try {
              (native as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
              return;
            } catch { /* showPicker not supported / blocked — fall through */ }
            native.focus();
            native.click();
          }}
          className="absolute inset-y-0 right-1 z-[1] grid w-7 place-items-center rounded text-content-muted hover:text-content-primary"
          title="Pick from calendar"
          aria-label="Open calendar"
        >
          📅
        </button>
        {/* Native date input — visually transparent but a real, clickable,
            full-height element pinned under the 📅 button so showPicker()
            and a direct tap both open the OS calendar. (The old version was
            h-0 w-0 pointer-events-none, so showPicker() had nothing to
            anchor to and the calendar never appeared.) */}
        <input
          ref={nativeRef}
          type="date"
          aria-hidden
          tabIndex={-1}
          className="absolute right-0 top-0 h-full w-9 cursor-pointer opacity-0"
          value={toISODateInput(value)}
          onChange={(e) => {
            if (!e.target.value) {
              setText('');
              onChange(null);
              return;
            }
            const iso = new Date(e.target.value).toISOString();
            setText(toDDMMYYYY(iso));
            onChange(iso);
            setErr(null);
          }}
        />
      </div>
      {err && <span className="text-[11px] text-danger">{err}</span>}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Date helpers                                                        */
/* -------------------------------------------------------------------- */

/** ISO/whatever → "dd-MM-yyyy" string. Empty for invalid input. */
function toDDMMYYYY(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

/**
 * "dd-MM-yyyy" → ISO string at midnight local time, or null when the
 * input doesn't parse to a real calendar date (e.g. "32-13-2024").
 */
function fromDDMMYYYY(s: string): string | null {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(s);
  if (!m) return null;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  if (mm < 1 || mm > 12) return null;
  if (dd < 1 || dd > 31) return null;
  const d = new Date(yyyy, mm - 1, dd);
  // Validate (e.g. 31 Feb won't round-trip back to month 1).
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) {
    return null;
  }
  return d.toISOString();
}

/** ISO → yyyy-MM-dd for the hidden native <input type="date">. */
function toISODateInput(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function FkSelect({
  field, value, onChange, selfResource, label,
}: {
  field: ApiFieldMeta;
  value: unknown;
  onChange: (next: unknown) => void;
  selfResource?: ApiResourceMeta;
  label: React.ReactNode;
}) {
  // Built on @kss/ui's SearchableSelect — type-to-filter UX, with the
  // FK options async-loaded from the target resource. Keeps the
  // existing fkResolver pipeline.
  return (
    <div className="flex flex-col gap-1">
      {label}
      <SearchableSelect
        value={value == null || value === '' ? '' : String(value)}
        onChange={(v) => onChange(v === '' ? null : Number(v))}
        loadOptions={async () => {
          const list = await getFkOptions(field.name, { selfResource });
          return list.map((o) => ({ value: String(o.id), label: o.label, description: o.description }));
        }}
        placeholder="— none —"
        searchPlaceholder="Type to search…"
      />
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */

export function seedForm(fields: ApiFieldMeta[], row: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (row && f.name in row) {
      out[f.name] = row[f.name];
    } else if (f.kind === 'boolean') {
      out[f.name] = false;
    } else {
      out[f.name] = null;
    }
  }
  // Default new rows to Active (status = 0) per the new numeric status enum.
  if (out.status == null) out.status = 0;
  return out;
}

export function coerce(form: Record<string, unknown>, fields: ApiFieldMeta[], isEdit: boolean): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const v = form[f.name];
    // Don't include empty-string / null values on create — let the
    // backend assign defaults / generate ids.
    if (!isEdit && (v === '' || v == null)) continue;
    out[f.name] = v;
  }
  // Status is the numeric enum the new swagger spec exposes (0/1/2).
  // Coerce defensively in case anything upstream wrote a string label.
  if (form.status != null && form.status !== '') {
    out.status = isActiveFromStatus(form.status);
  }
  return out;
}

/**
 * Async coerce variant — runs `coerce` then walks every image-shaped
 * field and uploads any `data:` URL via `/api/Image/upload` (G56 —
 * shipped 2026-05-29). Hosted URL replaces the inline base64 in the
 * payload so the entity row's narrow `imageUrl` column doesn't trip
 * SQL truncation.
 *
 * Use from save handlers in place of `coerce()`:
 *   const payload = await coerceWithImages(form, visibleFields, isEdit);
 */
export async function coerceWithImages(
  form: Record<string, unknown>,
  fields: ApiFieldMeta[],
  isEdit: boolean,
): Promise<Record<string, unknown>> {
  const out = coerce(form, fields, isEdit);
  const { persistImage } = await import('../../utils/persistImage');
  for (const f of fields) {
    const v = out[f.name];
    if (!isImageField(f.name)) continue;
    if (Array.isArray(v)) {
      out[f.name] = await Promise.all(
        v.map(async (item) =>
          typeof item === 'string' && item.startsWith('data:')
            ? (await persistImage(item)) ?? item
            : item,
        ),
      );
    } else if (typeof v === 'string' && v.startsWith('data:')) {
      try {
        out[f.name] = (await persistImage(v)) ?? v;
      } catch {
        // Upload failed — leave the data: URL in place so the user
        // at least sees a save attempt rather than a silent drop.
      }
    }
  }
  return out;
}

function isImageField(name: string): boolean {
  return /image|photo|avatar|logo|picture|thumbnail/i.test(name);
}

/**
 * Resolve the `<img src>` for a stored image URL.
 * - `data:` URL  → render as-is (preview of a not-yet-uploaded image)
 * - `http(s)://…` → render as-is
 * - `/uploads/…` → prepend the API host so the browser can fetch it
 *   (the entity's `imageUrl` column stores tenant-relative paths post-G56)
 *
 * Without this, `<img src="/uploads/...">` resolves against the panel's
 * own host (`localhost:5176` or `bill.kristysoftware.com`) instead of
 * the API host where uploads actually live → 404 → broken image.
 */
function resolveImageSrc(src: string): string {
  if (!src) return src;
  if (src.startsWith('data:') || /^https?:\/\//i.test(src)) return src;
  if (!src.startsWith('/')) return src;
  // apiClient.defaults.baseURL ends in `/api` in prod; strip it to get host.
  // In dev mode (`/api` → proxy) we leave the path relative — Vite's dev
  // proxy is also configured to forward `/uploads`.
  const apiBase = String(apiClient.defaults.baseURL ?? '');
  if (!apiBase || apiBase.startsWith('/')) return src;  // relative base (dev)
  return apiBase.replace(/\/api\/?$/, '') + src;
}

export function humanise(name: string): string {
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2') // letter→digit boundary: "last4" → "last 4"
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function safeJson(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function parseJsonSilently(text: string): unknown {
  if (text.trim() === '') return null;
  try {
    return JSON.parse(text);
  } catch {
    return text; // keep raw string until the user fixes it
  }
}

function toDatetimeLocal(v: unknown): string {
  if (typeof v !== 'string') return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  // YYYY-MM-DDTHH:mm
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(s: string): string | null {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}
